import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { useTheme } from "@material-ui/core/styles";
import { Box, Typography } from "@material-ui/core";
import { Lock } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { NodeActionsContext } from "./context/NodeActionsContext";
import withNodeActions from "./HOC/withNodeActions";
import ButtonEdge from "./Edges/ButtonEdge";
import { useStyles } from "./flowEditorStyles";
import useFlowEditor from "../hooks/useFlowEditor";
import NodeConfigModal from "./Modals/NodeConfigModal";
import FlowLintPanel from "./validation/FlowLintPanel";
import { lintFlow } from "./validation/flowLinter";
import FlowSimulatorPanel from "./simulator/FlowSimulatorPanel";
import useFlowSimulator from "./simulator/useFlowSimulator";
import FlowToolbar from "../components/FlowToolbar";
import NodePalette from "../components/NodePalette";
import UnsavedChangesDialog from "../components/UnsavedChangesDialog";
import StartNode from "./Nodes/StartNode";
import TextNode from "./Nodes/TextNode";
import ImageNode from "./Nodes/ImageNode";
import VideoNode from "./Nodes/VideoNode";
import AudioNode from "./Nodes/AudioNode";
import DocumentNode from "./Nodes/DocumentNode";
import ConditionNode from "./Nodes/ConditionNode";
import InputNode from "./Nodes/InputNode";
import MenuNode from "./Nodes/MenuNode";
import TagNode from "./Nodes/TagNode";
import RemoveTagNode from "./Nodes/RemoveTagNode";
import OpenAINode from "./Nodes/OpenAINode";
import HTTPRequestNode from "./Nodes/HTTPRequestNode";
import WaitNode from "./Nodes/WaitNode";
import TypingNode from "./Nodes/TypingNode";
import SetVariableNode from "./Nodes/SetVariableNode";
import TransferQueueNode from "./Nodes/TransferQueueNode";
import ContentNode from "./Nodes/ContentNode";
import ButtonsNode from "./Nodes/ButtonsNode";
import HsmTemplateNode from "./Nodes/HsmTemplateNode";

// ✅ Tipos de nodos — con HOC de acciones rápidas (duplicar / eliminar)
// Definidos a nivel de módulo para que la referencia sea estable entre renders (I-1)
const nodeTypes = {
  start:         withNodeActions(StartNode,         "start"),
  text:          withNodeActions(TextNode,          "text"),
  message:       withNodeActions(TextNode,          "message"),
  image:         withNodeActions(ImageNode,         "image"),
  video:         withNodeActions(VideoNode,         "video"),
  audio:         withNodeActions(AudioNode,         "audio"),
  document:      withNodeActions(DocumentNode,      "document"),
  condition:     withNodeActions(ConditionNode,     "condition"),
  input:         withNodeActions(InputNode,         "input"),
  menu:          withNodeActions(MenuNode,          "menu"),
  tag:           withNodeActions(TagNode,           "tag"),
  removeTag:     withNodeActions(RemoveTagNode,     "removeTag"),
  openai:        withNodeActions(OpenAINode,        "openai"),
  httpRequest:   withNodeActions(HTTPRequestNode,   "httpRequest"),
  wait:          withNodeActions(WaitNode,          "wait"),
  typing:        withNodeActions(TypingNode,        "typing"),
  setVariable:   withNodeActions(SetVariableNode,   "setVariable"),
  transferQueue: withNodeActions(TransferQueueNode, "transferQueue"),
  content:       withNodeActions(ContentNode,       "content"),
  buttons:       withNodeActions(ButtonsNode,       "buttons"),
  hsmTemplate:   withNodeActions(HsmTemplateNode,   "hsmTemplate"),
};

// ✅ Tipos de aristas — botón de borrado en el centro
const edgeTypes = {
  buttonEdge: ButtonEdge,
};

const FlowEditorReactFlow = ({ flowId, initialFlow, onSave, onCancel }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [lintPanelOpen, setLintPanelOpen] = useState(false);
  const reactFlowInstanceRef = useRef(null);
  // Canal del flujo (0371 WF5): filtra los nodos de la paleta y muestra el badge.
  const flowProvider = initialFlow?.provider || "baileys";

  const {
    nodes, edges, onNodesChange, onEdgesChange,
    flowName, setFlowName,
    saving,
    configModalOpen, setConfigModalOpen,
    selectedNode, setSelectedNode,
    hasUnsavedChanges,
    showCloseConfirmDialog, setShowCloseConfirmDialog,
    handleAddNode,
    handleDuplicateNode,
    handleDeleteNode,
    handleDeleteEdge,
    onConnect,
    handleNodeClick,
    handleSaveNodeConfig,
    handleSave,
    handleCancel,
    handleConfirmClose,
  } = useFlowEditor({ flowId, initialFlow, onSave, onCancel });

  // Validación estática (0383 FS-0): se recalcula con cada cambio del grafo.
  const lintResult = useMemo(
    () => lintFlow({ nodes, edges, provider: flowProvider }),
    [nodes, edges, flowProvider]
  );

  const handleFocusNode = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      const instance = reactFlowInstanceRef.current;
      if (!node || !instance) return;
      instance.setCenter(node.position.x + 100, node.position.y + 40, {
        zoom: 1.2,
        duration: 400,
      });
    },
    [nodes]
  );

  // Simulador (0383 FS-2). Se le pasa el grafo del editor tal cual está en pantalla, para
  // poder previsualizar también los cambios sin guardar.
  const getFlowSnapshot = useCallback(
    () => ({
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type,
        label: n.data?.label,
        position: n.position,
        data: n.data,
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || null,
        targetHandle: e.targetHandle || null,
      })),
    }),
    [nodes, edges]
  );

  const simulator = useFlowSimulator({
    flowId,
    provider: flowProvider,
    getFlowSnapshot,
  });

  const { open: simOpen, activeNodeId, visitedNodeIds } = simulator;

  // Auto-centrado en el nodo activo (FS-2.8): el canvas sigue al recorrido.
  useEffect(() => {
    if (simOpen && activeNodeId) handleFocusNode(activeNodeId);
  }, [simOpen, activeNodeId, handleFocusNode]);

  // Resaltado del recorrido (FS-2.7) por className, sin tocar el `data` que se persiste.
  const displayNodes = useMemo(() => {
    if (!simOpen) return nodes;
    return nodes.map((node) => {
      const isActive = node.id === activeNodeId;
      const isVisited = visitedNodeIds.includes(node.id);
      const className = isActive
        ? "sim-active"
        : isVisited
        ? "sim-visited"
        : "sim-dimmed";
      return { ...node, className };
    });
  }, [nodes, simOpen, activeNodeId, visitedNodeIds]);

  return (
    <Box className={classes.editorContainer}>
      <FlowToolbar
        flowName={flowName}
        setFlowName={setFlowName}
        nodes={nodes}
        hasUnsavedChanges={hasUnsavedChanges}
        saving={saving}
        onSave={handleSave}
        onCancel={handleCancel}
        onOpenDrawer={() => setMobileDrawerOpen(true)}
        provider={flowProvider}
        lintResult={lintResult}
        onToggleLintPanel={() => setLintPanelOpen((prev) => !prev)}
        onPreview={simulator.start}
        previewDisabled={
          simulator.loading || lintResult.hasBlockingErrors || nodes.length === 0
        }
        previewActive={simulator.open}
      />

      <Box className={classes.content}>
        <NodePalette
          onAddNode={handleAddNode}
          mobileOpen={mobileDrawerOpen}
          onDrawerClose={() => setMobileDrawerOpen(false)}
          provider={flowProvider}
        />

        {/* ✅ Canvas con React Flow — NodeActionsContext.Provider (I-3) */}
        <Box className={classes.reactFlowWrapper}>
          <NodeActionsContext.Provider
            value={{
              onDeleteNode: handleDeleteNode,
              onDuplicateNode: handleDuplicateNode,
              onDeleteEdge: handleDeleteEdge,
            }}
          >
            <ReactFlow
              nodes={displayNodes}
              edges={edges}
              onNodesChange={simOpen ? undefined : onNodesChange}
              onEdgesChange={simOpen ? undefined : onEdgesChange}
              onConnect={simOpen ? undefined : onConnect}
              onNodeClick={simOpen ? undefined : handleNodeClick}
              // Canvas bloqueado durante la previsualización (FS-2.9): editar mientras se
              // simula desincroniza lo que se ve de lo que se está ejecutando.
              nodesDraggable={!simOpen}
              nodesConnectable={!simOpen}
              elementsSelectable={!simOpen}
              onInit={(instance) => {
                reactFlowInstanceRef.current = instance;
              }}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              deleteKeyCode={["Backspace", "Delete"]}
              defaultEdgeOptions={{
                style: {
                  strokeWidth: 3,
                  stroke: theme.palette.type === "dark" ? "#757575" : "#b1b1b7",
                },
                animated: true,
                type: "buttonEdge",
              }}
              connectionLineStyle={{
                strokeWidth: 3,
                stroke: theme.palette.type === "dark" ? "#757575" : "#b1b1b7",
              }}
              snapToGrid={true}
              snapGrid={[20, 20]}
            >
              <Background
                variant="dots"
                gap={20}
                size={1.5}
                color={theme.palette.type === "dark" ? "#424242" : "#e0e0e0"}
                style={{ backgroundColor: theme.palette.background.default }}
              />
              <Controls
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.shadows[2],
                }}
              />
              <MiniMap
                nodeColor={(node) => {
                  if (node.type === "start") return "#667eea";
                  if (node.type === "message") return "#2196F3";
                  if (node.type === "condition") return "#FF9800";
                  if (node.type === "content") return "#4a148c";
                  return theme.palette.type === "dark" ? "#757575" : "#b1b1b7";
                }}
                maskColor={
                  theme.palette.type === "dark"
                    ? "rgba(0, 0, 0, 0.3)"
                    : "rgba(0, 0, 0, 0.05)"
                }
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                }}
                nodeBorderRadius={8}
              />
            </ReactFlow>
          </NodeActionsContext.Provider>

          {simOpen && (
            <Box className={classes.simLockBanner}>
              <Lock style={{ fontSize: 16 }} />
              <Typography variant="caption">
                {i18n.t("flowbuilders.simulator.canvasLocked") ||
                  "El área de trabajo está bloqueada durante la vista previa"}
              </Typography>
            </Box>
          )}

          <FlowLintPanel
            open={lintPanelOpen}
            onClose={() => setLintPanelOpen(false)}
            result={lintResult}
            onFocusNode={handleFocusNode}
          />
        </Box>

        <FlowSimulatorPanel
          open={simulator.open}
          loading={simulator.loading}
          session={simulator.session}
          visibleSteps={simulator.visibleSteps}
          isPlaying={simulator.isPlaying}
          provider={flowProvider}
          contact={simulator.contact}
          setContact={simulator.setContact}
          options={simulator.options}
          setOptions={simulator.setOptions}
          whatsappId={simulator.whatsappId}
          setWhatsappId={simulator.setWhatsappId}
          onReply={simulator.reply}
          onRestart={simulator.restart}
          onClose={simulator.stop}
        />
      </Box>

      <NodeConfigModal
        open={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedNode(null);
        }}
        node={selectedNode}
        onSave={handleSaveNodeConfig}
      />

      <UnsavedChangesDialog
        open={showCloseConfirmDialog}
        onClose={() => setShowCloseConfirmDialog(false)}
        onConfirm={handleConfirmClose}
      />
    </Box>
  );
};

export default FlowEditorReactFlow;
